import React, { useEffect, useRef, useState } from 'react';
import AIRPORTS from '../data/airports';

// Map city names and aliases to airport entries for quick display lookup
const CITY_TO_AIRPORT = (() => {
  const m = new Map();
  for (const airport of AIRPORTS) {
    m.set(airport.city.toLowerCase(), airport);
    if (airport.aliases) {
      for (const alias of airport.aliases) {
        if (!m.has(alias.toLowerCase())) m.set(alias.toLowerCase(), airport);
      }
    }
  }
  return m;
})();

const MAX_SUGGESTIONS = 8;

function getDisplayText(cityValue) {
  if (!cityValue) return '';
  const airport = CITY_TO_AIRPORT.get(cityValue.toLowerCase());
  return airport ? `${airport.city} (${airport.code}) - ${airport.name}` : cityValue;
}

function searchAirports(query) {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  const scored = [];
  for (const airport of AIRPORTS) {
    const codeExact      = airport.code.toLowerCase() === q;
    const codePrefix     = airport.code.toLowerCase().startsWith(q);
    const cityStartsWith = airport.city.toLowerCase().startsWith(q);
    const cityContains   = airport.city.toLowerCase().includes(q);
    const nameContains   = airport.name.toLowerCase().includes(q);
    const countryContains = airport.country.toLowerCase().startsWith(q);
    const aliasMatch     = (airport.aliases || []).some((a) => a.toLowerCase().includes(q));

    if (!codeExact && !codePrefix && !cityContains && !nameContains && !countryContains && !aliasMatch) continue;

    // Score: exact code = 100, code prefix = 90, city starts = 80, city contains = 60,
    //        alias = 55, name = 40, country = 20
    let score = 0;
    if (codeExact)           score = 100;
    else if (codePrefix)     score = 90;
    else if (cityStartsWith) score = 80;
    else if (cityContains)   score = 60;
    else if (aliasMatch)     score = 55;
    else if (nameContains)   score = 40;
    else if (countryContains) score = 20;

    scored.push({ airport, score });
  }

  return scored
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_SUGGESTIONS)
    .map((entry) => entry.airport);
}

export default function AirportSearchInput({ value, onChange, onSelect, placeholder, id, label, defaultSuggestions, excludeAirport }) {
  const [inputText, setInputText] = useState(() => getDisplayText(value));
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const containerRef = useRef(null);

  // Filter function to exclude a specific airport
  const filterExcludedAirport = (airports) => {
    if (!excludeAirport) return airports;
    const excludeCity = excludeAirport.toLowerCase();
    return airports.filter(airport => airport.city.toLowerCase() !== excludeCity);
  };

  // Sync display when value changes externally (e.g. deal card click)
  useEffect(() => {
    setInputText(getDisplayText(value));
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
        // Reset display to committed value if user clicked away mid-type
        setInputText(getDisplayText(value));
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen, value]);

  const handleInputChange = (e) => {
    const text = e.target.value;
    setInputText(text);
    setHighlightedIndex(-1);

    const results = filterExcludedAirport(searchAirports(text));
    setSuggestions(results);
    setIsOpen(results.length > 0);

    // If input is cleared, propagate empty value
    if (!text.trim()) {
      onChange('');
    }
  };

  const commitSelection = (airport) => {
    setInputText(`${airport.city} (${airport.code}) - ${airport.name}`);
    setSuggestions([]);
    setIsOpen(false);
    setHighlightedIndex(-1);
    onChange(airport.city);
    if (onSelect) onSelect(airport);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        const results = filterExcludedAirport(searchAirports(inputText));
        if (results.length > 0) {
          setSuggestions(results);
          setIsOpen(true);
          setHighlightedIndex(0);
          e.preventDefault();
        }
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
        commitSelection(suggestions[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlightedIndex(-1);
      setInputText(getDisplayText(value));
    }
  };

  const handleFocus = () => {
    // On focus, clear the display text so user can type freely
    setInputText('');
    const results = searchAirports('');
    if (results.length === 0) {
      // Show default suggestions (if provided) or popular airports when input is empty
      const defaultList = defaultSuggestions || AIRPORTS.slice(0, MAX_SUGGESTIONS);
      setSuggestions(filterExcludedAirport(defaultList));
    } else {
      setSuggestions(filterExcludedAirport(results));
    }
    setIsOpen(true);
  };

  const handleItemMouseDown = (e, airport) => {
    // Prevent input blur before we can commit the selection
    e.preventDefault();
    commitSelection(airport);
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightedIndex];
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  return (
    <div className="airport-search-input" ref={containerRef}>
      {label && <label htmlFor={id}>{label}</label>}
      <div className="airport-search-input__field">
        <input
          id={id}
          ref={inputRef}
          type="text"
          autoComplete="off"
          spellCheck="false"
          enterKeyHint="search"
          value={inputText}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder || 'City or airport code'}
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={isOpen ? `${id}-listbox` : undefined}
          aria-activedescendant={
            isOpen && highlightedIndex >= 0
              ? `${id}-option-${highlightedIndex}`
              : undefined
          }
          role="combobox"
        />
        {inputText && (
          <button
            type="button"
            className="airport-search-input__clear"
            aria-label="Clear"
            tabIndex={-1}
            onMouseDown={(e) => {
              e.preventDefault();
              setInputText('');
              setSuggestions([]);
              setIsOpen(false);
              onChange('');
              inputRef.current?.focus();
            }}
          >
            ×
          </button>
        )}
      </div>

      {isOpen && suggestions.length > 0 && (
        <ul
          id={`${id}-listbox`}
          className="airport-search-input__dropdown"
          role="listbox"
          ref={listRef}
          aria-label={`${label || 'Airport'} suggestions`}
        >
          {suggestions.map((airport, idx) => (
            <li
              key={`${airport.code}-${airport.name}`}
              id={`${id}-option-${idx}`}
              role="option"
              aria-selected={idx === highlightedIndex}
              className={`airport-search-input__option${idx === highlightedIndex ? ' airport-search-input__option--highlighted' : ''}`}
              onMouseDown={(e) => handleItemMouseDown(e, airport)}
              onMouseEnter={() => setHighlightedIndex(idx)}
            >
              <span className="airport-search-input__code">{airport.code}</span>
              <span className="airport-search-input__info">
                <span className="airport-search-input__city">{airport.city}</span>
                <span className="airport-search-input__name">{airport.name}</span>
              </span>
              <span className="airport-search-input__country">{airport.country}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
